/**
 * Route-level code splitting that survives a deploy.
 *
 * Every screen is fetched on first visit. Two things make that fetch fail, and
 * both show the same browser message, "Failed to fetch dynamically imported
 * module":
 *
 *  - In production, a deploy replaces index.html and gives every chunk a new
 *    content hash. A tab opened before the deploy still asks for the old
 *    filename, which is no longer on the server.
 *  - In development, Vite appends a `?t=<timestamp>` to a module it has just
 *    rebuilt. A tab holding the previous timestamp asks for a URL the dev
 *    server has moved past.
 *
 * Neither is a real failure: the code exists, under a different name. The only
 * way to learn that name is a fresh index.html, so after one retry we reload
 * once. The flag stops a genuinely broken chunk from reloading forever.
 */

import { lazy } from 'react';
import type { ComponentType, LazyExoticComponent } from 'react';

const RELOAD_FLAG = 'leadgen:chunk-reloaded';

/** Recognises the stale-chunk failure across browsers. */
export function isChunkLoadError(error: unknown): boolean {
  const message =
    error instanceof Error ? error.message : typeof error === 'string' ? error : '';
  return (
    // Chrome, Edge
    /failed to fetch dynamically imported module/i.test(message) ||
    // Firefox
    /error loading dynamically imported module/i.test(message) ||
    // Safari
    /importing a module script failed/i.test(message) ||
    /unable to preload css/i.test(message)
  );
}

/** sessionStorage throws in some privacy modes; a failed read just means "no". */
function alreadyReloaded(): boolean {
  try {
    return sessionStorage.getItem(RELOAD_FLAG) === '1';
  } catch {
    return false;
  }
}

function markReloaded(): void {
  try {
    sessionStorage.setItem(RELOAD_FLAG, '1');
  } catch {
    /* ignore: the reload still helps, we just cannot remember it */
  }
}

function clearReloaded(): void {
  try {
    sessionStorage.removeItem(RELOAD_FLAG);
  } catch {
    /* ignore */
  }
}

/* eslint-disable @typescript-eslint/no-explicit-any --
   React declares lazy() as <T extends ComponentType<any>>. Narrowing the
   constraint here makes every route component fail to assign to it. */
export function lazyRoute<T extends ComponentType<any>>(
  load: () => Promise<{ default: T }>,
): LazyExoticComponent<T> {
  return lazy(async () => {
    try {
      const loaded = await load();
      // Back on a current build: let the next stale chunk reload again.
      clearReloaded();
      return loaded;
    } catch (error) {
      if (!isChunkLoadError(error)) throw error;

      try {
        // A transient network blip needs nothing more than asking again.
        return await load();
      } catch (secondError) {
        if (alreadyReloaded()) throw secondError;
        markReloaded();
        window.location.reload();
        // The page is going away; never resolve, so no error flashes first.
        return await new Promise<never>(() => {});
      }
    }
  });
}
